import { z } from 'zod';
import { User } from 'shared/src/domain/user/user-types';
import { MediaPlan, SortDirection } from 'shared/src/domain/media-buy/media-buy-types';
import {
  ChannelSelection,
  Platform,
  TacticSelection,
  UnitPriceType,
  UnitPriceTypeNames
} from 'shared/src/domain/line-item/line-item-channels';
import { dateToUTC, MoneyNumber } from 'shared/src/utils/zod/zod-utils';
import {
  AgDateFilter,
  AgNumberFilter,
  AgSetFilter,
  AgTextFilter
} from 'shared/src/domain/ag-grid-filter-types';
import { ColumnVO } from '@ag-grid-community/core';
import { PacingBlockV2FromDB } from 'shared/src/date-string-utils/types';

export const PacingType = z.union([z.literal('lifetime'), z.literal('custom')]);

export type PacingType = z.infer<typeof PacingType>;

export const LineItemType = z.union([
  z.literal('standard'),
  z.literal('management_fee'),
  z.literal('zero_dollar'),
  z.literal('zero_margin')
]);
export type LineItemType = z.infer<typeof LineItemType>;
export const lineItemTypeOptions = LineItemType.options.map(option => option.value);

export const lineItemTypeDisplayNames: Record<LineItemType, string> = {
  standard: 'Standard',
  management_fee: 'Management Fee',
  zero_dollar: 'Zero Dollar',
  zero_margin: 'Zero Margin'
};

export const LineItem = z.object({
  id: z.string(),
  line_item_type: LineItemType,
  strategy_id: z.string(),
  purchase_order_number: z
    .string()
    .regex(/^\d+$/, 'Purchase order number must be numeric')
    .nullable()
    .optional(),
  name: z.string(),
  channel: ChannelSelection,
  tactic: TacticSelection,
  unit_price_type: UnitPriceType,
  geo: z.string(),
  targeting: z.string(),
  description: z.string(),
  audience: z.string(),
  ad_formats: z.string(),
  price: z.coerce.number(),
  pacing_type: PacingType,
  pacing_details: z.array(PacingBlockV2FromDB),
  unit_price: z.coerce.number().nullish(),
  effective_unit_price: z.coerce.number().nullable(),
  media_budget: MoneyNumber,
  referral_commission_rate: z.coerce.number(),
  effective_price: z.coerce.number(),
  referral_commission_amount: z.coerce.number(),
  agency_markup_rate: z.coerce.number(),
  gross_price: z.coerce.number(),
  gross_unit_price: z.coerce.number().nullable(),
  planned_margin: z.coerce.number().nullish(),
  estimated_units: z.coerce.number().nullish(),
  planned_unit_cost: z.coerce.number().nullish(),
  target_margin: z.coerce.number().nullish(),
  target_unit_price: z.coerce.number().nullish(),
  target_unit_cost: z.coerce.number().nullish(),
  pacing_graph: z.array(z.coerce.number()).nullish(),
  start_date: dateToUTC,
  end_date: dateToUTC,
  media_platforms: z.array(Platform),
  media_traders: z.array(User),
  media_plans: z.array(MediaPlan),
  is_deleted: z.boolean(),
  notes: z.string().max(1000, 'Team notes must be 1000 characters or less').nullish()
});

export type LineItem = z.infer<typeof LineItem>;

export const CampaignLineItem = LineItem.omit({
  media_plans: true,
  channel: true,
  tactic: true,
  unit_price_type: true
}).extend({
  campaign_id: z.string(),
  campaign_name: z.string(),
  campaign_number: z.string(),
  campaign_source: z.string().nullable(),
  campaign_flight_date: z.coerce.date().nullable(),
  campaign_stage: z.string().nullable(),
  account_id: z.string().nullable(),
  account_name: z.string().nullable(),
  agency_id: z.string().nullable(),
  agency_name: z.string().nullable(),
  lead_account_user: z.string().nullable(),
  senior_media_trader: z.string().nullable(),
  client_success_director: z.string().nullable(),
  channel_name: z.string(),
  tactic_name: z.string(),
  channel_tactic: z.string(),
  unit_price_type_name: UnitPriceTypeNames,
  monthly_rollover: z.boolean()
});
export type CampaignLineItem = z.infer<typeof CampaignLineItem>;

export const PartialLineItem = LineItem.partial()
  .omit({
    id: true,
    strategy_id: true,
    media_plans: true
  })
  .extend({ id: z.string(), strategy_id: z.string() });
export type PartialLineItem = z.infer<typeof PartialLineItem>;

export const LineItemBottomPosition = z.object({
  type: z.literal('bottom')
});

export const LineItemAfterPosition = z.object({
  type: z.literal('after'),
  itemId: z.string()
});

export const LineItemPosition = z.union([LineItemBottomPosition, LineItemAfterPosition]);
export type LineItemPosition = z.infer<typeof LineItemPosition>;

export const NewLineItemDraft = z.object({
  type: z.literal('new'),
  data: PartialLineItem,
  position: LineItemPosition.optional()
});
export type NewLineItemDraft = z.infer<typeof NewLineItemDraft>;

export const UpdatedLineItemDraft = z.object({
  type: z.literal('update'),
  data: PartialLineItem
});
export type UpdatedLineItemDraft = z.infer<typeof UpdatedLineItemDraft>;

export const LineItemSortColumn = z.union([
  //sr media trader
  z.literal('name'),
  z.literal('campaign_id'),
  z.literal('campaign_number'),
  z.literal('campaign_name'),
  z.literal('campaign_stage'),
  z.literal('account_name'),
  z.literal('agency_name'),
  z.literal('channel'),
  z.literal('tactic'),
  z.literal('channel_tactic'),
  z.literal('start_date'),
  z.literal('end_date'),
  z.literal('price'),
  z.literal('effective_price'),
  z.literal('referral_commission_rate'),
  z.literal('referral_commission_amount'),
  z.literal('unit_price_type'),
  z.literal('geo'),
  z.literal('targeting'),
  z.literal('audience'),
  z.literal('media_budget'),
  z.literal('media_platforms'),
  z.literal('estimated_units'),
  z.literal('planned_unit_cost'),
  z.literal('effective_unit_price'),
  z.literal('planned_margin'),
  z.literal('num_media_buys'),
  z.literal('delivery_pacing'),
  z.literal('percent_units_delivered'),
  z.literal('units_delivered'),
  z.literal('on_pace_units'),
  z.literal('target_units'),
  z.literal('units_remaining'),
  z.literal('spend_pacing'),
  z.literal('percent_budget_spent'),
  z.literal('media_spend'),
  z.literal('on_pace_spend'),
  z.literal('budget_remaining'),
  z.literal('margin_variance'),
  z.literal('margin_actual'),
  z.literal('unit_cost_variance'),
  z.literal('unit_cost_actual'),
  z.literal('planned_unit_cost'),
  z.literal('percent_revenue_delivered'),
  z.literal('billable_revenue'),
  z.literal('on_pace_revenue'),
  z.literal('target_revenue'),
  z.literal('revenue_remaining'),
  z.literal('lead_account_user'),
  z.literal('media_traders'),
  z.literal('senior_media_trader'),
  z.literal('client_success_director'),
  z.literal('purchase_order_number'),
  z.literal('monthly_capped_actual_revenue'),
  z.literal('effective_monthly_capped_actual_revenue'),
  z.literal('lifetime_capped_actual_revenue'),
  z.literal('effective_lifetime_capped_actual_revenue'),
  z.literal('uncapped_revenue')
]);
export type LineItemSortColumn = z.infer<typeof LineItemSortColumn>;
export const lineItemSortColumns = LineItemSortColumn.options.map(option => option.value);

export const LineItemPlan = z.object({
  active_buys_outside_plan_count: z.coerce.number(),
  active_media_plans_count: z.coerce.number()
});
export type LineItemPlan = z.infer<typeof LineItemPlan>;

// Grouping support schemas
export const LineItemAggregateCols = z.object({
  active_buys_outside_plan_count: z.coerce.number(),
  active_media_plans_count: z.coerce.number(),
  delivery_pacing: z.coerce.number(),
  percent_units_delivered: z.coerce.number(),
  units_delivered: z.coerce.number(),
  on_pace_units: z.coerce.number(),
  target_units: z.coerce.number(),
  units_remaining: z.coerce.number(),
  spend_pacing: z.coerce.number(),
  percent_budget_spent: z.coerce.number(),
  media_spend: z.coerce.number(),
  on_pace_spend: z.coerce.number(),
  budget_remaining: z.coerce.number(),
  margin_actual: z.coerce.number(),
  margin_variance: z.coerce.number(),
  planned_margin: z.coerce.number(),
  target_margin: z.coerce.number(),
  target_margin_variance: z.coerce.number(),
  unit_cost_variance: z.coerce.number(),
  unit_cost_actual: z.coerce.number(),
  planned_unit_cost: z.coerce.number(),
  percent_revenue_delivered: z.coerce.number(),
  billable_revenue: z.coerce.number(),
  on_pace_revenue: z.coerce.number(),
  target_revenue: z.coerce.number(),
  revenue_remaining: z.coerce.number(),
  monthly_capped_actual_revenue: z.coerce.number(),
  effective_monthly_capped_actual_revenue: z.coerce.number(),
  lifetime_capped_actual_revenue: z.coerce.number(),
  effective_lifetime_capped_actual_revenue: z.coerce.number(),
  uncapped_revenue: z.coerce.number(),
  num_media_buys: z.coerce.number(),
  num_media_plans: z.coerce.number(),
  campaign_budget: z.coerce.number()
});

export const lineItemAggregateColumnNames = Object.keys(
  LineItemAggregateCols.shape
) as (keyof z.infer<typeof LineItemAggregateCols>)[];

export const LineItemAggregateRow = z
  .object({
    group: z.literal(true),
    group_key: z.string(),
    count: z.coerce.number()
  })
  .extend(CampaignLineItem.partial().shape)
  .extend(LineItemAggregateCols.shape);

export type LineItemAggregateRow = z.infer<typeof LineItemAggregateRow>;

export const LineItemListRow = z
  .object({
    group: z.literal(false),
    count: z.number().optional()
  })
  .extend(CampaignLineItem.shape)
  .extend(LineItemAggregateCols.shape);
export type LineItemListRow = z.infer<typeof LineItemListRow>;

export const LineItemTableRow = z.discriminatedUnion('group', [
  LineItemAggregateRow,
  LineItemListRow
]);
export type LineItemTableRow = z.infer<typeof LineItemTableRow>;

export const LineItemSort = z.object({
  colId: LineItemSortColumn,
  sort: SortDirection
});
export type LineItemSort = z.infer<typeof LineItemSort>;

export const LineItemsFilterTypes = z.object({
  campaign_name: AgTextFilter.optional(),
  campaign_source: AgTextFilter.optional(),
  campaign_number: AgTextFilter.optional(),
  campaign_stage: AgSetFilter.optional(),
  account_name: AgSetFilter.optional(),
  agency_name: AgSetFilter.optional(),
  line_item_type: AgSetFilter.optional(),
  campaign_budget: AgNumberFilter.optional(),
  line_item_name: AgTextFilter.optional(),
  price: AgNumberFilter.optional(),
  effective_price: AgNumberFilter.optional(),
  referral_commission_rate: AgNumberFilter.optional(),
  referral_commission_amount: AgNumberFilter.optional(),
  media_budget: AgNumberFilter.optional(),
  channel_tactic: AgSetFilter.optional(),
  pacing_type: AgSetFilter.optional(),
  monthly_rollover: AgSetFilter.optional(),
  unit_price_type_name: AgSetFilter.optional(),
  start_date: AgDateFilter.optional(),
  end_date: AgDateFilter.optional(),
  media_traders: AgSetFilter.optional(),
  num_media_plans: AgNumberFilter.optional(),
  units_remaining: AgNumberFilter.optional(),
  units_delivered: AgNumberFilter.optional(),
  delivery_pacing: AgNumberFilter.optional(),
  target_units: AgNumberFilter.optional(),
  on_pace_units: AgNumberFilter.optional(),
  media_spend: AgNumberFilter.optional(),
  on_pace_spend: AgNumberFilter.optional(),
  spend_pacing: AgNumberFilter.optional(),
  budget_remaining: AgNumberFilter.optional(),
  margin_variance: AgNumberFilter.optional(),
  margin_actual: AgNumberFilter.optional(),
  planned_margin: AgNumberFilter.optional(),
  target_margin: AgNumberFilter.optional(),
  target_margin_variance: AgNumberFilter.optional(),
  planned_unit_cost: AgNumberFilter.optional(),
  effective_unit_price: AgNumberFilter.optional(),
  target_unit_cost: AgNumberFilter.optional(),
  unit_cost_variance: AgNumberFilter.optional(),
  unit_cost_actual: AgNumberFilter.optional(),
  on_pace_revenue: AgNumberFilter.optional(),
  target_revenue: AgNumberFilter.optional(),
  revenue_remaining: AgNumberFilter.optional(),
  billable_revenue: AgNumberFilter.optional(),
  percent_revenue_delivered: AgNumberFilter.optional(),
  percent_budget_spent: AgNumberFilter.optional(),
  percent_units_delivered: AgNumberFilter.optional(),
  geo: AgTextFilter.optional(),
  targeting: AgTextFilter.optional(),
  audience: AgTextFilter.optional(),
  lead_account_user: AgSetFilter.optional(),
  senior_media_trader: AgSetFilter.optional(),
  client_success_director: AgSetFilter.optional(),
  media_platforms: AgSetFilter.optional(),
  plan_status: AgSetFilter.optional(),
  notes: AgTextFilter.optional(),
  active_platform_buys: AgSetFilter.optional(),
  purchase_order_number: AgTextFilter.optional(),
  monthly_capped_actual_revenue: AgNumberFilter.optional(),
  effective_monthly_capped_actual_revenue: AgNumberFilter.optional(),
  lifetime_capped_actual_revenue: AgNumberFilter.optional(),
  effective_lifetime_capped_actual_revenue: AgNumberFilter.optional(),
  uncapped_revenue: AgNumberFilter.optional()
});
export type LineItemsFilterTypes = z.infer<typeof LineItemsFilterTypes>;

export const PlanStatusFilterValues = z.union([z.literal('complete'), z.literal('pending')]);
export type PlanStatusFilterValues = z.infer<typeof PlanStatusFilterValues>;

export const ActivePlatformBuysFilterValues = z.union([
  z.literal('pending_plan'),
  z.literal('pending_start'),
  z.literal('active_outside_dates'),
  z.literal('none_active'),
  z.literal('some_active'),
  z.literal('all_active')
]);
export type ActivePlatformBuysFilterValues = z.infer<typeof ActivePlatformBuysFilterValues>;

export const LineItemListRequest = z.object({
  page: z.coerce.number(),
  search: z.string().optional(),
  sort: LineItemSort.optional(),
  pageSize: z.coerce.number().optional(),
  filters: LineItemsFilterTypes,
  groupKeys: z.string().array().optional(),
  rowGroupCols: z.custom<ColumnVO>().array().optional()
});

export type LineItemListRequest = z.infer<typeof LineItemListRequest>;

export const LineItemOverallResult = z.object({
  total: z.coerce.number(),
  overall_price: z.coerce.number().nullable(),
  overall_effective_price: z.coerce.number().nullable(),
  overall_media_budget: z.coerce.number().nullable(),
  overall_actual_media_spend: z.coerce.number().nullable(),
  overall_actual_delivered_units: z.coerce.number().nullable(),
  overall_billable_revenue: z.coerce.number().nullable(),
  overall_delivery_pacing: z.coerce.number().nullable(),
  overall_on_pace_units: z.coerce.number().nullable(),
  overall_target_units: z.coerce.number().nullable(),
  overall_units_remaining: z.coerce.number().nullable(),
  overall_percent_units_delivered: z.coerce.number().nullable(),
  overall_unit_cost_actual: z.coerce.number().nullable(),
  overall_planned_unit_cost: z.coerce.number().nullable(),
  overall_unit_cost_variance: z.coerce.number().nullable(),
  overall_margin_actual: z.coerce.number().nullable(),
  overall_margin_variance: z.coerce.number().nullable(),
  overall_planned_margin: z.coerce.number().nullable(),
  overall_target_margin_variance: z.coerce.number().nullable(),
  overall_target_margin: z.coerce.number().nullable(),
  overall_on_pace_spend: z.coerce.number().nullable(),
  overall_spend_pacing: z.coerce.number().nullable(),
  overall_budget_remaining: z.coerce.number().nullable(),
  overall_percent_budget_spent: z.coerce.number().nullable(),
  overall_on_pace_revenue: z.coerce.number().nullable(),
  overall_target_revenue: z.coerce.number().nullable(),
  overall_revenue_remaining: z.coerce.number().nullable(),
  overall_percent_revenue_delivered: z.coerce.number().nullable(),
  overall_monthly_capped_actual_revenue: z.coerce.number().nullable(),
  overall_effective_monthly_capped_actual_revenue: z.coerce.number().nullable(),
  overall_lifetime_capped_actual_revenue: z.coerce.number().nullable(),
  overall_effective_lifetime_capped_actual_revenue: z.coerce.number().nullable(),
  overall_uncapped_revenue: z.coerce.number().nullable(),
  earliest_start_date: dateToUTC.nullable(),
  latest_end_date: dateToUTC.nullable()
});
export type LineItemOverallResult = z.infer<typeof LineItemOverallResult>;

export const LineItemListResult = z.object({
  line_items: z.array(LineItemTableRow),
  overall: LineItemOverallResult.nullable()
});

export type LineItemListResult = z.infer<typeof LineItemListResult>;

// Draft is not needed for line item updates
export const LineItemChangeDraft = z.union([NewLineItemDraft, UpdatedLineItemDraft]);
export type LineItemChangeDraft = z.infer<typeof LineItemChangeDraft>;

// Helper function to format line item type for display
export function formatLineItemType(type: LineItemType): string {
  return lineItemTypeDisplayNames[type];
}
